import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { isNull } from "../../../../../shared/lib/html5-software-library/src/ranj/util/TypeUtil";
import AppModel from "../../../../../shared/src/ranj/app/core/AppModel";
import PlayMode from "../../../../../shared/src/ranj/equalture/model/PlayMode";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import GameConfig from "./GameConfig";
import GameModelEvent from "./GameModelEvent";
import PuzzleModel from "./PuzzleModel";
import PuzzleModelEvent from "./PuzzleModelEvent";
import PuzzleStatus from "./PuzzleStatus";
import TrackingController from "./TrackingController";

export default class GameModel extends EventDispatcher {

    /**
     * @param {soma.EventDispatcher} dispatcher
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController
     * @param {AppModel} appModel
     */
    constructor(gameConfig, trackingController, appModel) {
        super();
        this._gameConfig = gameConfig;
        this._trackingController = trackingController;
        this._appModel = appModel;

        this._propagateEvent = this._propagateEvent.bind(this);
        this._handlePuzzleStatus = this._handlePuzzleStatus.bind(this);
        this._handlePuzzleMove = this._handlePuzzleMove.bind(this);
        this.reset();
    }

    get puzzleCount() {
        return this.puzzles.length;
    }

    get puzzleIndex() {
        return this._puzzleIndex;
    }

    get failedAttempts() {
        return this._puzzleModel ? this._puzzleModel.failedAttemptCount : 0;
    }

    reset() {
        this._puzzleIndex = -1;
        this._elapsedTime = 0;
    }

    get puzzles() {
        if (!this._puzzles) {
            //determine which puzzles to use:
            let puzzleIds;
            switch (this._appModel.playMode) {
                case PlayMode.DEMO:
                    puzzleIds = this._gameConfig.getValue("modes").demo;
                    break;
                default:
                    puzzleIds = this._gameConfig.getValue("modes")._default;
                    break;
            }
            const all = this._gameConfig.getValue("puzzles");
            this._puzzles = puzzleIds.map(puzzleId=> {
                return all.find(puzzleCfg => puzzleCfg.id == puzzleId);
            });
        }
        return this._puzzles;
    }

    doNextPuzzle() {
        if (!this.hasNextPuzzle()) {
            return false;
        } else {
            this.initPuzzle(this._puzzleIndex + 1);
            return true;
        }
    }

    applySavedState(obj) {
        this._elapsedTime = obj.elapsed;
        switch (obj.puzzle.status) {
            case PuzzleStatus.STARTED:
                //restore puzzle state if it was ongoing
                this.initPuzzle(obj.index, obj.puzzle);
                break;
            case PuzzleStatus.FAILED:
                //reattempt if it was failed: restore, but reset locations
                obj.puzzle.state = null;
                this.initPuzzle(obj.index, obj.puzzle);
                break;
            default:
                //go to next if it was timed out or completed
                this._puzzleIndex = obj.index;
                this.doNextPuzzle();
                break;
        }
        this.addElapsedTime(0);
    }

    initTutorialPuzzle() {
        this._puzzleModel = new PuzzleModel(this._gameConfig.getValue("tutorial.puzzle"));
        return this._puzzleModel;
    }

    /**
     * 
     * @param {Number} index 
     * @param {Object} puzzleState - generic object containing savegame data for puzzle
     */
    initPuzzle(index, puzzleState = null) {
        this._destroyPuzzle();
        this._puzzleIndex = index;
        this._puzzleModel = new PuzzleModel(this.puzzles[index]);
        this._addPuzzleEventListeners();
        if (puzzleState) {
            this._puzzleModel.setFromObject(puzzleState);
        }
        this.dispatchEvent(GameModelEvent.PUZZLE, this._puzzleModel);
    }

    _addPuzzleEventListeners() {
        this._puzzleModel.addEventListener(PuzzleModelEvent.MOVE_INVALID, this._propagateEvent);
        this._puzzleModel.addEventListener(PuzzleModelEvent.ELAPSED_TIME, this._propagateEvent);
        this._puzzleModel.addEventListener(PuzzleModelEvent.MOVE, this._handlePuzzleMove);
        this._puzzleModel.addEventListener(PuzzleModelEvent.STATUS, this._handlePuzzleStatus);
    }

    _destroyPuzzle() {
        if (!isNull(this._puzzleModel)) {
            this._puzzleModel.removeEventListener(PuzzleModelEvent.MOVE_INVALID, this._propagateEvent);
            this._puzzleModel.removeEventListener(PuzzleModelEvent.ELAPSED_TIME, this._propagateEvent);
            this._puzzleModel.removeEventListener(PuzzleModelEvent.MOVE, this._handlePuzzleMove);
            this._puzzleModel.removeEventListener(PuzzleModelEvent.STATUS, this._handlePuzzleStatus);
        }
    }

    _handlePuzzleMove(e) {
        //track
        const { type, count, actor } = e.data;
        const totalCount = this._puzzleModel.totalMoveCount;
        this._trackingController.trackMove(this._puzzleIndex, totalCount, type, actor, this.elapsedTime);
        this._propagateEvent(e);
    }
    _handlePuzzleStatus(e) {
        switch (e.data) {
            case PuzzleStatus.STARTED:
                if (this._puzzleModel.failedAttemptCount == 0) {
                    if (this._puzzleIndex == 0) {
                        this._trackingController.trackProgress(ProgressValue.STARTED, this.elapsedTime);
                    }
                    this._trackingController.trackStartPuzzle(this._puzzleIndex, this.puzzle.availableTime, this.puzzle.minimumMoves, this.elapsedTime);
                }
                break;
            case PuzzleStatus.FAILED:
                this._trackingController.trackFailPuzzle(this._puzzleIndex, this.elapsedTime);
                break;
            case PuzzleStatus.COMPLETED:
                this._trackingController.trackSolvePuzzle(this._puzzleIndex, this.elapsedTime);
                if (!this.hasNextPuzzle()) {
                    this._trackingController.trackProgress(ProgressValue.COMPLETED, this.elapsedTime);
                }
                break;
            case PuzzleStatus.TIMEOUT:
                break;
        }
        this._propagateEvent(e);
    }

    hasNextPuzzle() {
        return this._puzzleIndex < this.puzzles.length - 1;
    }

    startPuzzle() {
        this._puzzleModel.start();
    }

    resetPuzzle() {
        this._puzzleModel.reset();
    }

    _propagateEvent(e) {
        this.dispatchEvent(e.type, e.data);
    }

    get puzzle() {
        return this._puzzleModel;
    }

    get failedConstraints() {
        return this._puzzleModel.failedConstraints;
    }

    get totalMoveCount() {
        return this._puzzleModel ? this._puzzleModel.totalMoveCount : 0;
    }

    //--------------------
    //   TIME RELATED
    //--------------------


    addElapsedTime(dt) {
        this._elapsedTime += dt;
        this.dispatchEvent(GameModelEvent.ELAPSED_TIME, this._elapsedTime);
        this.puzzle.addElapsedTime(dt);
    }

    get elapsedTime() {
        return this._elapsedTime;
    }

    get elapsedPuzzleTime() {
        return this.puzzle.elapsedTime;
    }
    get remainingPuzzleTime() {
        return this.puzzle.remainingTime;
    }

}
